import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

import { Loader2, SlidersHorizontal, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { StatusEnum } from "@/api/lctApISchemas";

export type FilterConfig =
  | {
      label: string;
      options: { label: string; value: string }[];
      selected: string | StatusEnum | undefined;
      onChange: React.Dispatch<React.SetStateAction<string | StatusEnum | undefined>>;
      optionsLoading?: boolean;
    }
  | undefined;

export function FilterBadges({ filters }: { filters: FilterConfig[] }) {
  return (
    <div className="flex flex-wrap gap-2 pl-2">
      {filters
        .filter((filter) => filter?.selected)
        .map((filter) => {
          if (!filter) return null;
          const opt = filter.options.find((o) => o.value === filter.selected);
          return (
            <Badge
              key={filter.label + filter.selected}
              variant="default"
              className="cursor-pointer"
              onClick={() => filter.onChange(undefined)}
            >
              {opt?.label} ×
            </Badge>
          );
        })}
    </div>
  );
}

export function FilterDrawer({ filters }: { filters: FilterConfig[] }) {
  const [open, setOpen] = useState(false);
  const handleClearAll = () => {
    filters.forEach((filter) => {
      if (filter) {
        filter.onChange(undefined);
      }
    });
    setOpen(false);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen} modal={false}>
      <SheetTrigger asChild>
        <Button variant="outline">
          <SlidersHorizontal className="mr-2 h-4 w-4" />
          Filters
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="h-full overflow-y-auto md:w-[400px]">
        <SheetHeader className="mb-4">
          <SheetTitle>Filter Results</SheetTitle>
          <SheetDescription className="mb-4">Apply filters to narrow down your results.</SheetDescription>
        </SheetHeader>
        {filters.map((filter) => {
          if (!filter) return null;
          if (filter.optionsLoading) {
            return <Loader2 className="h-4 w-4 animate-spin" key={filter.label} />;
          }
          return (
            <div key={filter.label} className="mb-4 flex flex-col gap-2 p-2 pb-4 text-sm">
              <div className="mb-2 flex items-center justify-between">
                {filter.label}
                {filter.selected ? (
                  <Badge variant="outline" className="cursor-pointer text-sm font-normal" onClick={() => filter.onChange(undefined)}>
                    <X className="mr-2 h-4 w-4" />
                    Clear
                  </Badge>
                ) : null}
              </div>
              <div className="flex flex-col gap-1">
                <RadioGroup
                  value={filter.selected ?? ""}
                  onValueChange={(val) => filter.onChange(val === "" ? undefined : val)}
                  className="flex flex-col gap-1"
                >
                  {filter.options.map((opt) => (
                    <div key={opt.value} className="flex items-center space-x-2">
                      <RadioGroupItem id={`${filter.label}-${opt.value}`} value={opt.value} />
                      <label htmlFor={`${filter.label}-${opt.value}`} className="cursor-pointer">
                        {opt.label}
                      </label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            </div>
          );
        })}
        <div className="mt-4 flex gap-2">
          <Button variant="ghost" onClick={handleClearAll}>
            Clear All
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
